import { Flex, InputNumber, Space, Typography } from '@arkestro/arkestro-design-system';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { CadenceUnitSelect } from '@components/Agreements/shared/CadenceUnitSelect/CadenceUnitSelect';
import { MAX_CADENCE_BY_UNIT, MIN_CADENCE } from '@components/Agreements/utils/agreementsConstants';
import { useCadenceUnitClamp } from '@components/Agreements/utils/useCadenceUnitClamp';
import { unitPlaceholderKey } from '@components/shared/UnitSelect/unitPlaceholderKey';
import { EventSeriesCadenceUnit } from 'types/EventSeries';
import styles from './RecurrencePanel.module.scss';
import sharedStyles from '@components/RfxDrawers/shared/SharedStyles.module.scss';

export const RecurrencePanel = () => {
  const { t } = useTranslation('common');
  const { getValues, setValue } = useFormContext();
  const cadence: number | undefined = useWatch({ name: 'cadence' });
  const cadenceUnit: EventSeriesCadenceUnit = useWatch({ name: 'cadenceUnit' }) ?? 'days';

  const handleCadenceUnitChange = useCadenceUnitClamp(
    () => getValues('cadence'),
    max => setValue('cadence', max, { shouldValidate: true })
  );

  return (
    <Flex className={sharedStyles.panel} gap={16} vertical>
      <Typography className={sharedStyles.panelHeader}>{t('rfx_drawer.recurrence_panel_title')}</Typography>
      <Flex gap={4} vertical>
        <Flex align='center' gap={4}>
          <span className={sharedStyles.required}>*</span>
          <Typography className={sharedStyles.text}>{t('rfx_drawer.recurrence_cadence_label')}</Typography>
        </Flex>
        <Space.Compact>
          <Controller
            name="cadence"
            render={({ field: { value, onChange, onBlur } }) => (
              <InputNumber
                className={styles.cadenceInput}
                controls={false}
                data-testid='cadence-input'
                max={MAX_CADENCE_BY_UNIT[cadenceUnit]}
                min={MIN_CADENCE}
                onBlur={onBlur}
                onChange={(val: number | string | null) => onChange(val == null || val === '' ? undefined : Number(val))}
                placeholder={t(unitPlaceholderKey(cadenceUnit))}
                precision={0}
                step={1}
                value={value}
              />
            )}
            // antd InputNumber clamps to [min, max] on blur, so we rely on the
            // component for range enforcement and only validate presence here.
            rules={{ required: true }}
          />
          <Controller
            name="cadenceUnit"
            render={({ field: { value, onChange } }) => (
              <CadenceUnitSelect
                count={cadence}
                dataTestId='cadence-unit-select'
                onChange={handleCadenceUnitChange(onChange)}
                value={value}
              />
            )}
          />
        </Space.Compact>
      </Flex>
    </Flex>
  );
};
